import { Flex, Form, Switch, Tooltip, Typography } from "antd"
import React from "react"

import { type SETTINGS_PERMISSIONS_COLUMNS } from "../constants/columns.ts"
import type { InheritedRow } from "../helpers/section-layout.ts"
import { getColKey, getSectionTitle } from "../helpers/utils.ts"
import type { ActionRecord } from "../types.ts"
import type { PermissionDisplayMap } from "../types/permissions.types.ts"

const { Text } = Typography

type PermissionsInheritedRowProps = {
  row: InheritedRow
  columns: ReadonlyArray<(typeof SETTINGS_PERMISSIONS_COLUMNS)[number]>
  gridTemplateColumns: string
  displayMap?: PermissionDisplayMap | null
}

/**
 * A row whose rights belong to another module. It shows what that module
 * grants, live as the form changes, and saves nothing of its own: the switch
 * that changes it is in that module's section.
 */
export const PermissionsInheritedRow = ({
  row,
  columns,
  gridTemplateColumns,
  displayMap
}: PermissionsInheritedRowProps) => {
  const watched = Form.useWatch<ActionRecord | undefined>([
    row.parentKey,
    row.subKey
  ])
  const source = getSectionTitle(row.sourceKey, displayMap)
  const note = `Uses ${source} permissions`

  return (
    <div className={"border-b border-border"}>
      <div
        className={"grid py-1.5 px-4 items-center"}
        style={{ gridTemplateColumns }}
      >
        <Flex vertical>
          <Text ellipsis>{row.label}</Text>
          <Text type="secondary" className={"text-xs capitalize"}>
            {note}
          </Text>
        </Flex>
        {columns.map((col) => {
          const action = Object.keys(row.actions).find(
            (key) => getColKey(key, row.actions) === col.key
          )
          if (!action) {
            return (
              <Flex justify="center" key={col.key}>
                <Text type="secondary">—</Text>
              </Flex>
            )
          }
          const checked = (watched?.[action] ?? row.actions[action]) === true
          return (
            <Flex justify="center" key={col.key}>
              <Tooltip title={`Set in ${source}`}>
                <Switch
                  size="small"
                  checked={checked}
                  disabled
                  aria-label={`${row.label} — ${col.label}, set in ${source}`}
                />
              </Tooltip>
            </Flex>
          )
        })}
      </div>
    </div>
  )
}
